import { findMostBookableFloor } from "../../Booking-Form/functions/form.functions";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { FloorAvailability } from "../../FloorManager/typings/floor-inventory.entity";
import { IPlaceInventory } from "../../../components/FacilityManager/Domain/Types/FloorPlan/PlaceInventory.type";
import { IZoneInventory } from "../../../components/FacilityManager/Domain/Types/FloorPlan/ZoneInventory.type";
import type { BookableBookingObjectDto } from "../typings/resource-calendar.types";

export type ResolvedResourceLocationFloor = {
  locationInventoryId: number | null;
  floorInventoryId: number | undefined;
};

export function resolveLocationAndFloorFromSchedules(
  schedules: BookingScheduleInterface[]
): ResolvedResourceLocationFloor {
  const first = schedules[0];
  if (!first) {
    return { locationInventoryId: null, floorInventoryId: undefined };
  }

  const inv = first.bookingInventory as IPlaceInventory | IZoneInventory | undefined;
  const locationInventoryIdFromInventory =
    inv && "locationInventoryId" in inv ? inv.locationInventoryId : undefined;
  const locationInventoryId =
    first.locationInventoryId ??
    locationInventoryIdFromInventory ??
    inv?.locationInventory?.id ??
    null;

  const floorInventoryId = inv?.floorInventoryId ?? inv?.floorInventory?.id ?? undefined;

  return { locationInventoryId, floorInventoryId };
}

/**
 * Same as {@link resolveLocationAndFloorFromSchedules}, then falls back to the bookable row so
 * `selectedLocation` is set for confirm even when schedules are empty or omit location ids.
 */
export function resolveResourceBookLocationForConfirm(
  schedules: BookingScheduleInterface[],
  row: BookableBookingObjectDto
): ResolvedResourceLocationFloor {
  const fromSchedules = resolveLocationAndFloorFromSchedules(schedules);
  const locationInventoryId = fromSchedules.locationInventoryId ?? row.locationInventoryId ?? null;
  const floorInventoryId = fromSchedules.floorInventoryId ?? row.floorInventoryId ?? undefined;
  return { floorInventoryId, locationInventoryId };
}

/**
 * After `fetchBookingFloors`, Redux has `floors.inventory` but often no `currentFloorIndex`.
 * `FormConfirmFloorPlan` / `handleFloorFetch` need a concrete floor row to load the plan.
 */
export function resolveCurrentFloorIndexForResourceBookConfirm(
  inventory: FloorAvailability[],
  floorInventoryId: number | undefined
): number {
  if (inventory.length === 0) {
    return 0;
  }
  if (floorInventoryId != null) {
    const match = inventory.findIndex(f => f.id === floorInventoryId);
    if (match >= 0) {
      return match;
    }
  }
  return findMostBookableFloor(inventory).mostBookableFloorIndex;
}
